import React from "react";
import {
  WarningOutlined,
  InfoOutlined,
  ErrorOutline,
  CheckCircleOutline,
} from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import { useLanguageSettings } from "store/user-settings/user-settings-store";
import { useGetGlobalAlert } from "services/input-core/release-notes/global-alert";
import { useSubscription } from "store/auth/auth-store";
import { useLocation } from "react-router-dom";
import { replaceKeyWithDate } from "utils/functions";

const useStyles = makeStyles({
  alert: {
    padding: "10px 16px",
    display: "flex",
    alignItems: "center",
    minWidth: "80%",
  },
  success: {
    backgroundColor: "#EDF7ED",
    color: "#155724",
  },
  info: {
    backgroundColor: "#E5F6FD",
    color: "#0c5460",
  },
  warning: {
    backgroundColor: "#FFF4E5",
    color: "#856404",
  },
  error: {
    backgroundColor: "#f8d7da",
    color: "#721c24",
  },
  icon: {
    marginRight: "10px",
  },
  message: {
    fontSize: "14px",
    color: "inherit",
  },
});

const GlobalAlert: React.FC = () => {
  const classes = useStyles();
  const { language } = useLanguageSettings();
  const { subscription } = useSubscription();
  const location = useLocation();

  const shouldFetchAlerts = location.pathname !== "/auth/login";

  const { data } = useGetGlobalAlert(
    {
      subscription: subscription?.subscriptionId,
    },
    {
      enabled: shouldFetchAlerts,
    },
  );

  const getAlertClass = (notificationType: string) => {
    switch (notificationType?.toLowerCase()) {
      case "success":
        return classes.success;
      case "info":
        return classes.info;
      case "warning":
        return classes.warning;
      case "error":
        return classes.error;
      default:
        return "";
    }
  };

  const getIcon = (notificationType: string) => {
    switch (notificationType?.toLowerCase()) {
      case "success":
        return <CheckCircleOutline className={classes.icon} />;
      case "info":
        return <InfoOutlined className={classes.icon} />;
      case "warning":
        return <WarningOutlined className={classes.icon} />;
      case "error":
        return <ErrorOutline className={classes.icon} />;
      default:
        return null;
    }
  };

  const getDescription = (
    languageTexts: { type: string; description: string }[],
    selectedLanguage: string,
    timeZoneDate?: string,
  ) => {
    const adjustedLanguage =
      selectedLanguage === "it" ? "ITA" : selectedLanguage.toUpperCase();
    const descriptionForLanguage = languageTexts.find(
      (text) => text.type === adjustedLanguage,
    );
    const description = descriptionForLanguage
      ? descriptionForLanguage.description
      : languageTexts[0].description;

    return timeZoneDate
      ? replaceKeyWithDate(description, "KEY-DATE-RELEASE", timeZoneDate)
      : description;
  };

  return (
    <>
      {location.pathname !== "/app/documentation" &&
        data?.map(
          (notification, index: number) =>
            notification?.active && (
              <div
                key={index}
                className={`${classes.alert} ${getAlertClass(
                  notification.notificationType,
                )}`}
              >
                {getIcon(notification.notificationType)}
                <span className={classes.message}>
                  {getDescription(
                    notification.languageTexts,
                    language,
                    notification.timeZoneByNotification?.[0]?.timeZoneDate,
                  )}
                </span>
              </div>
            ),
        )}
    </>
  );
};

export default GlobalAlert;
